import {
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Label,
  Title
} from '@patternfly/react-core';
import * as React from 'react';

import {
  SURVEY_COMMENT_GROUPS,
  SURVEY_SECTIONS,
  SURVEY_TEXT_QUESTIONS,
  Submission,
  answeredRatio,
  booleanAnswer,
  isAnswered,
  respondentLabel,
  submissionComment
} from '@app/model/Survey';
import { AnswerCell, QuestionLabel } from '@app/Classes/SurveyParts';
import { TranslatableText } from '@app/components/TranslatableText';
import { ddMMyyyyFormat, ddMMyyyyHHmmFormat } from '@app/utils/functions';

/** A percentage from a fraction, for the two completion figures. */
export const answeredPercent = (fraction?: number): string =>
  fraction === undefined || fraction === null ? '—' : `${Math.round(fraction * 100)}%`;

interface SubmissionDetailProps {
  submission: Submission;
  index: number;
}

/**
 * One respondent's submission in full: who they are, what they answered, and what they wrote.
 *
 * **Identified data, `admin` only.** It is reached from two places - a matrix column heading and an
 * expanded respondent row - and is one component so that neither can end up showing a different
 * subset of it than the other.
 *
 * Only the questions this respondent actually answered are drawn. A form with fifty questions and a
 * student who answered twelve renders as twelve, not as twelve answers among thirty-eight dashes,
 * which is the difference between a readable card and a page of nothing. What they skipped is still
 * countable - the "answered" figure at the top is over every question this application knows about.
 */
export const SubmissionDetail: React.FunctionComponent<SubmissionDetailProps> = ({
  submission,
  index
}) => {
  const written = SURVEY_TEXT_QUESTIONS.filter(({ field }) => {
    const value = submission[field];
    return typeof value === 'string' && value.trim().length > 0;
  });

  const comments = SURVEY_COMMENT_GROUPS
    .map(group => ({ ...group, comment: submissionComment(submission, group) }))
    .filter(({ comment }) => comment.original.trim().length > 0);

  return (
    <div className="cas-survey-detail">
      <DescriptionList isHorizontal isCompact isFluid>
        <DescriptionListGroup>
          <DescriptionListTerm>Respondent</DescriptionListTerm>
          <DescriptionListDescription>
            {respondentLabel(submission, index)}
            {submission.email && <span className="cas-survey-muted"> · {submission.email}</span>}
            {submission.username && <span className="cas-survey-muted"> · {submission.username}</span>}
          </DescriptionListDescription>
        </DescriptionListGroup>
        {(submission.company || submission.jobTitle) && (
          <DescriptionListGroup>
            <DescriptionListTerm>Employer</DescriptionListTerm>
            <DescriptionListDescription>
              {[submission.jobTitle, submission.company].filter(Boolean).join(', ')}
              {submission.companyType && <span className="cas-survey-muted"> · {submission.companyType}</span>}
              {submission.companyRelationship && (
                <span className="cas-survey-muted"> · {submission.companyRelationship}</span>
              )}
            </DescriptionListDescription>
          </DescriptionListGroup>
        )}
        <DescriptionListGroup>
          <DescriptionListTerm>Answered from</DescriptionListTerm>
          <DescriptionListDescription>
            {/* The respondent's own country and language, not the class's - a student can attend a
                class in another country, and this is the one they answered with. */}
            {submission.country?.name ?? 'Not recorded'}
            {submission.language && <span className="cas-survey-muted"> · in {submission.language.name}</span>}
          </DescriptionListDescription>
        </DescriptionListGroup>
        {submission.entitlementType && (
          <DescriptionListGroup>
            <DescriptionListTerm>Entitlement</DescriptionListTerm>
            <DescriptionListDescription>{submission.entitlementType}</DescriptionListDescription>
          </DescriptionListGroup>
        )}
        <DescriptionListGroup>
          <DescriptionListTerm>Submitted</DescriptionListTerm>
          <DescriptionListDescription>
            {ddMMyyyyFormat(submission.surveyDate) || '—'}
            {submission.startDate && (
              <span className="cas-survey-muted"> · started {ddMMyyyyHHmmFormat(submission.startDate)}</span>
            )}
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Completeness</DescriptionListTerm>
          <DescriptionListDescription>
            {/* Two different measurements, so both are named. Upstream's is over upstream's own
                form, which asks things this service does not store; ours is over the questions
                this application knows about. */}
            {answeredPercent(answeredRatio(submission))} of the questions here
            {submission.completionRatio !== undefined && submission.completionRatio !== null && (
              <span className="cas-survey-muted">
                {' '}· {answeredPercent(submission.completionRatio)} of the form upstream
              </span>
            )}
            {submission.progress && <span className="cas-survey-muted"> · {submission.progress}</span>}
          </DescriptionListDescription>
        </DescriptionListGroup>
        {(submission.surveySlug || submission.surveyVersion) && (
          <DescriptionListGroup>
            <DescriptionListTerm>Form</DescriptionListTerm>
            <DescriptionListDescription>
              <code>{submission.surveySlug}</code>
              {submission.surveyVersion && <span className="cas-survey-muted"> v{submission.surveyVersion}</span>}
            </DescriptionListDescription>
          </DescriptionListGroup>
        )}
      </DescriptionList>

      <div className="cas-survey-answers">
        {SURVEY_SECTIONS.map(section => {
          const answered = section.questions.filter(question => isAnswered(submission, question));
          if (answered.length === 0) return null;

          return (
            <div key={section.key} className="cas-survey-answer-group">
              <Title headingLevel="h4" size="md">{section.title}</Title>
              <ul>
                {answered.map(question => (
                  <li key={String(question.field)}>
                    <AnswerCell question={question} submission={submission} />
                    <QuestionLabel question={question} />
                    {/* Only a yes/no is spelled out. A rating's swatch already carries the number,
                        and printing it again beside it turned every line into "5 ... 5.0". */}
                    {question.kind === 'yesno' && (
                      <span className="cas-survey-muted">
                        {booleanAnswer(submission, question) ? 'yes' : 'no'}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {written.length > 0 && (
        <div className="cas-survey-written">
          <Title headingLevel="h4" size="md">Written answers</Title>
          <DescriptionList isCompact>
            {written.map(({ field, label }) => (
              <DescriptionListGroup key={String(field)}>
                <DescriptionListTerm>{label}</DescriptionListTerm>
                <DescriptionListDescription>{submission[field] as string}</DescriptionListDescription>
              </DescriptionListGroup>
            ))}
          </DescriptionList>
        </div>
      )}

      {comments.length > 0 && (
        <div className="cas-survey-written">
          <Title headingLevel="h4" size="md">Comments</Title>
          {comments.map(({ field, label, comment }) => (
            <blockquote key={String(field)} className="cas-survey-quote">
              {/* The box's label shares the control line with the switch, so the two things that
                  are about the comment sit together above it. */}
              <TranslatableText
                comment={comment}
                prefix={<Label isCompact variant="outline">{label}</Label>}
                what={`the ${label.toLowerCase()} comment`}
              />
            </blockquote>
          ))}
        </div>
      )}
    </div>
  );
};
